/** Build-time config from Vite env vars (see .env.example). Safe to read in tests (Bun also exposes import.meta.env). */
const env: Record<string, string | undefined> = ((import.meta as unknown as { env?: Record<string, string | undefined> }).env ?? {});

/**
 * Sign-in (docs/AUTH.md). "dev": the "pick who you are" person picker (`x-dev-user`, localhost only); email sign-in is
 * still reachable from it. "real": Google (Firebase, identity only) or a 6-digit email code, then our own session
 * token as `Authorization: Bearer`. `VITE_AUTH_MODE=real` (or the old alias `firebase`) picks real, `dev` picks dev;
 * unset, a production build is real and a dev server is dev.
 */
export type AuthMode = "dev" | "real";

/**
 * Does this build carry the in-browser sample data (src/mock)? vite.config.ts defines __CC_MOCK__ true only for a mock or demo build, so a
 * real build drops the mock code from its main chunk (about 0.3 MB). Bun (tests) and the dev server leave it undefined: mock stays on.
 */
declare const __CC_MOCK__: boolean | undefined;
export const MOCK_BUILD: boolean = typeof __CC_MOCK__ === "undefined" ? true : __CC_MOCK__;
/** The in-browser mock API and sign-in. False in every real build, whatever else is set. */
export const IS_MOCK: boolean = MOCK_BUILD && env.VITE_API_MODE === "mock";
export type ApiMode = "http" | "mock";

export function authModeFrom(v: string | undefined, prod: boolean): AuthMode {
  if (v === "real" || v === "firebase") return "real";
  if (v === "dev") return "dev";
  return prod ? "real" : "dev";
}

export const config = {
  authMode: authModeFrom(env.VITE_AUTH_MODE, env.PROD === "true" || (env.PROD as unknown) === true),
  apiMode: (IS_MOCK ? "mock" : "http") as ApiMode,
  /** Demo build: one-tap "Try it as the owner", in-browser sample data, no account. */
  demo: env.VITE_DEMO === "1",
  apiUrl: (env.VITE_API_URL || "http://127.0.0.1:8787").replace(/\/+$/, ""),
  /**
   * Review links (docs/source/research/reviews-and-trust.md). Unset (the default): Settings → "Leave a review" and
   * the web note stay hidden, so nobody lands on an unclaimed Trustpilot page. VITE_APP_STORE_ID is the numeric id.
   */
  trustpilotUrl: env.VITE_TRUSTPILOT_URL || "",
  appStoreId: env.VITE_APP_STORE_ID || "",
  /** Nag at home (core nag.ts): Hit Em Up's compose link (VITE_HITEMUP_COMPOSE_URL, e.g. hitemup://compose). Unset: "Copy the message". */
  hitemupComposeUrl: env.VITE_HITEMUP_COMPOSE_URL || "",
  /** Firebase web config for "Continue with Google". The button shows only when all three are set. */
  firebase: {
    apiKey: env.VITE_FIREBASE_API_KEY || "",
    authDomain: env.VITE_FIREBASE_AUTH_DOMAIN || "",
    projectId: env.VITE_FIREBASE_PROJECT_ID || "",
  },
};
